import React, { useState, useEffect, useMemo } from "react";
import { agentService } from "../../services/agentService";
import { useBanks, useMaterials } from "../../query/hooks";
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  CalendarIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import LogoLoader from "../Common/LogoLoader";

const ValidationHistory = () => {
  const navigate = useNavigate();
  const materialsQuery = useMaterials();
  const banksQuery = useBanks(true);
  const materials = materialsQuery.data || [];
  const userBanks = useMemo(
    () => banksQuery.data?.data?.bankDetails || banksQuery.data?.bankDetails || [],
    [banksQuery.data]
  );
  const [validationHistory, setValidationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSendingDeliveries, setIsSendingDeliveries] = useState(false);
  const [filter, setFilter] = useState("all"); // all, today, week, month
  /** Seller checkout: pending = bin still open; confirmed = seller completed sale (Sale record) */
  const [saleFilter, setSaleFilter] = useState("confirmed");
  const [message, setMessage] = useState("");
  const [selectedPaymentBank, setSelectedPaymentBank] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (userBanks.length > 0 && !selectedPaymentBank) {
      setSelectedPaymentBank(userBanks[0]);
    }
  }, [userBanks, selectedPaymentBank]);

  const fetchData = async () => {
    try {
      console.log("Starting to fetch validation history data...");
      
      // Fetch validation history, materials, and user banks
      const historyData = await agentService.getValidationHistory();
      console.log("Validation history data:", historyData);
      setValidationHistory(historyData);
    } catch (error) {
      console.error("Error fetching validation history:", error);
      setMessage("Error loading validation history");
    } finally {
      setLoading(false);
    }
  };

  const getFilteredHistory = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    const list =
      saleFilter === "confirmed"
        ? validationHistory?.data?.completedSales || []
        : validationHistory?.data?.bins || [];

    return list.filter((validation) => {
      const validationDate = new Date(validation.createdAt);

      let timeMatch = true;
      switch (filter) {
        case "today":
          timeMatch = validationDate >= today;
          break;
        case "week":
          timeMatch = validationDate >= weekAgo;
          break;
        case "month":
          timeMatch = validationDate >= monthAgo;
          break;
        default:
          timeMatch = true;
      }

      return timeMatch;
    });
  };

  const getTotalEarnings = (history) => {
    if (!history || !Array.isArray(history)) {
      return 0;
    }
    return history.reduce((total, validation) => {
      return total + (validation.totalProfit || 0);
    }, 0);
  };

  const getConfirmedEarnings = () => {
    const rows = validationHistory?.data?.completedSales || [];
    return getTotalEarnings(rows);
  };

  const getPendingEarnings = () => {
    const bins = validationHistory?.data?.bins || [];
    return getTotalEarnings(bins);
  };

  const getCompletedSalesEarnings = () => {
    const rows = validationHistory?.data?.completedSales || [];
    return getTotalEarnings(rows);
  };

  const getTotalValidations = (history) => {
    if (!history || !Array.isArray(history)) {
      return 0;
    }
    return history.length;
  };

  if (loading || materialsQuery.isLoading || banksQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LogoLoader />
      </div>
    );
  }

  const handleBankSelection = (bankId) => {
    const selectedBank = userBanks.find(bank => bank._id === bankId);
    setSelectedPaymentBank(selectedBank);
    console.log("Selected payment bank:", selectedBank);
  };

  const handleDeliverBin = async () => {
    try {
      setIsSendingDeliveries(true);

      if (!selectedPaymentBank) {
        setMessage("Please select a payment bank before delivering products.");
        setIsSendingDeliveries(false);
        return;
      }

      const saleIds = filteredHistory
        .filter(
          (row) =>
            row.kind === "seller_completed_sale" &&
            !row.agentDelivery
        )
        .map((row) => String(row.saleId || row._id));

      if (saleIds.length === 0) {
        setMessage(
          "No seller-confirmed sales are ready for delivery. They may already be submitted, or switch to Confirmed to select orders."
        );
        setIsSendingDeliveries(false);
        return;
      }

      await agentService.createDelivery({
        saleIds,
        selectedPaymentBank: selectedPaymentBank._id,
      });
      // Backend excludes agentDelivery != null from validation history, so after submitting,
      // the items disappear from this list and show up in delivery history.
      setMessage("Delivery submitted successfully! Redirecting to delivery history...");
      fetchData();
      setTimeout(() => {
        navigate("/agent/delivery-history");
      }, 2000);
    } catch (error) {
      console.error("Error creating delivery:", error);
      setMessage(error.response?.data?.message || "Error completing delivery");
    } finally {
      setIsSendingDeliveries(false);
    }
  };

  const handleConfirmSale = async (binId) => {
    try {
      await agentService.confirmSale(binId);
      setMessage("Sale confirmed successfully!");
      // Refresh the data
      fetchData();
    } catch (error) {
      console.error("Error confirming sale:", error);
      setMessage(error.response?.data?.message || "Error confirming sale");
    }
  };

  const filteredHistory = getFilteredHistory();
  const deliverableCount = filteredHistory.filter(
    (row) => row.kind === "seller_completed_sale" && !row.agentDelivery
  ).length;
  const totalValidations = getTotalValidations(filteredHistory);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Validation History
        </h1>
        <p className="text-gray-600">
          Track your validation history and earnings from bin validations.
        </p>
      </div>

      {/* Payment Bank Selection */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900 mb-4">Select Payment Bank</h3>
        {userBanks.length > 0 ? (
          <div className="space-y-3">
            {userBanks.map((bank) => (
              <label
                key={bank._id}
                className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                  selectedPaymentBank?._id === bank._id
                    ? 'border-blue-300 bg-blue-100 hover:bg-blue-200'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="paymentBank"
                  value={bank._id}
                  checked={selectedPaymentBank?._id === bank._id}
                  onChange={(e) => handleBankSelection(e.target.value)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <div className="ml-3">
                  <p className="font-medium text-gray-900">
                    {bank.bankName}
                  </p>
                  <p className="text-sm text-gray-600">
                    {bank.accountName} - {bank.accountNumber}
                  </p>
                </div>
              </label>
            ))}
          </div>
        ) : (
          <div className="text-sm text-blue-700">
            <p>No bank accounts found. Please add a bank account first.</p>
            <button
              onClick={() => navigate("/banks")}
              className="mt-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Add Bank Account
            </button>
          </div>
        )}
      </div>

      {/* Message */}
      {message && (
        <div className="bg-red-100 text-red-700 border border-red-200 p-4 rounded-md">
          <div className="flex items-center">
            <XCircleIcon className="h-5 w-5 mr-2" />
            {message}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Total Validations
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {totalValidations}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Confirmed (seller) earnings
              </p>
              <p className="text-2xl font-bold text-gray-900">
                ₦{getConfirmedEarnings().toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <CurrencyDollarIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Pending (seller) earnings
              </p>
              <p className="text-2xl font-bold text-gray-900">
                ₦{getPendingEarnings().toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CurrencyDollarIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Seller completed earnings
              </p>
              <p className="text-2xl font-bold text-gray-900">
                ₦{getCompletedSalesEarnings().toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CurrencyDollarIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Price per Kg
              </p>
              <p className="text-2xl font-bold text-gray-900">
                ₦{validationHistory?.data?.currentFeePerKg?.toFixed(2) || "0.00"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Validation History
          </h2>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">
                Seller sale status:
              </span>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setSaleFilter("pending")}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    saleFilter === "pending"
                      ? "bg-yellow-600 text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Pending
                </button>
                <button
                  type="button"
                  onClick={() => setSaleFilter("confirmed")}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    saleFilter === "confirmed"
                      ? "bg-green-600 text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Confirmed
                </button>
              </div>
            </div>
            
            {/* Time Filter */}
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
        </div>

        {filteredHistory?.length === 0 ? (
          <div className="text-center py-12">
            <ClockIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {saleFilter === "confirmed"
                ? "No seller-confirmed sales yet"
                : "No pending seller checkouts"}
            </h3>
            <p className="text-gray-600">
              {saleFilter === "confirmed"
                ? "After the seller completes checkout, the order appears here. You can submit delivery only from Confirmed."
                : "These are validated bins where the seller has not finished checkout yet."}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredHistory?.map((validation) => (
              <ValidationHistoryCard
                key={validation._id || validation.saleId}
                validation={validation}
                materials={materials}
                onConfirmSale={handleConfirmSale}
                saleFilter={saleFilter}
              />
            ))}
          </div>
        )}
      </div>
      {saleFilter === "confirmed" && (
        <div className="space-y-2">
          <button
            onClick={handleDeliverBin}
            disabled={!selectedPaymentBank || deliverableCount === 0 || isSendingDeliveries}
            className={`w-full font-medium py-2 px-4 rounded-md transition-colors ${
              selectedPaymentBank && deliverableCount > 0 && !isSendingDeliveries
                ? "bg-green-600 hover:bg-green-700 text-white"
                : "bg-gray-400 text-gray-200 cursor-not-allowed"
            }`}
          >
            {isSendingDeliveries
              ? "Submitting delivery..."
              : `Deliver materials (${deliverableCount})`}
          </button>
          {!selectedPaymentBank && (
            <p className="text-sm text-red-600 text-center">
              Please select a payment bank above before delivering materials
            </p>
          )}
          {selectedPaymentBank && deliverableCount === 0 && (
            <p className="text-sm text-gray-600 text-center">
              No seller-confirmed sales are ready for delivery.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Validation History Card Component
const ValidationHistoryCard = ({ validation, materials, onConfirmSale, saleFilter }) => {
  const [expanded, setExpanded] = useState(false);
  const isSellerCompleted = validation.kind === "seller_completed_sale";

  // const getMaterialName = (materialId) => {
  //   const material = materials.find((m) => m._id === materialId);
  //   return material?.name || "Unknown Material";
  // };

  // const getMaterialPrice = (materialId) => {
  //   const material = materials.find((m) => m._id === materialId);
  //   return material?.price_per_kg || 0;
  // };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">
            {isSellerCompleted
              ? `Sale #${validation._id?.slice(-10) || "N/A"}`
              : `Bin #${validation._id?.slice(-10) || "N/A"}`}
          </h3>
          <p className="text-sm text-gray-600">
            {new Date(validation.createdAt).toLocaleDateString()} at{" "}
            {new Date(validation.createdAt).toLocaleTimeString()}
          </p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-green-600">
            ₦{validation.totalProfit?.toFixed(2) || "0.00"}
          </p>
          <p className="text-sm text-gray-600">
            {validation.materials?.length || 0} materials • {validation.totalQuantity || 0}kg
          </p>
          <p className="text-xs text-gray-500">
            Fee: ₦{validation.currentFeePerKg || 20}/kg
          </p>
        </div>
      </div>

      {/* Seller Info */}
      <div className="flex items-center mb-4">
        <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
        <span className="text-sm text-gray-600">
          Seller: {validation.user?.firstName || ""}{" "}
          {validation.user?.lastName || ""}
          {!validation.user?.firstName && !validation.user?.lastName ? "N/A" : ""}
        </span>
      </div>

      {/* Materials Summary */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            Materials Validated:
          </span>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-sm text-green-600 hover:text-green-700"
          >
            {expanded ? "Hide Details" : "Show Details"}
          </button>
        </div>

        {expanded && (
          <div className="mt-3 space-y-2">
            {validation.materials?.map((item, index) => (
              <div
                key={index}
                className="flex justify-between items-center text-sm"
              >
                <span className="text-gray-700">
                  {item.material?.name || "Material"}
                </span>
                <div className="flex items-center space-x-4">
                  <span className="text-gray-600">{item.quantity}kg</span>
                  <span className="text-green-600 font-medium">
                    ₦{item.agentProfit?.toFixed(2) || "0.00"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Status and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center flex-wrap gap-1">
          <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2" />
          <span className="text-sm text-green-600 font-medium">
            {isSellerCompleted ? "Seller sale: Confirmed" : "Seller sale: Pending"}
          </span>
          {isSellerCompleted ? (
            <>
              <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                Payment {validation.sellerPaymentStatus || "processing"}
              </span>
              {validation.agentDelivery ? (
                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Submitted for delivery
                </span>
              ) : (
                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-900">
                  Ready to deliver
                </span>
              )}
            </>
          ) : (
            <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Waiting for seller checkout
            </span>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center space-x-2">
          {/* No action buttons for agents - only sellers can confirm sales */}
        </div>
      </div>
    </div>
  );
};

export default ValidationHistory;
